import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import React, { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import feedbackLottieAnimations from 'app-components/assets/animations/feedback/feedbackThumbsUpDown.json';
import QChoiceChips from 'components/QuickenControls/QChoiceChips';

export type FeedbackInitialView = 'loading' | 'rating' | 'details';

export type FeedbackReasonOption<TReason extends string> = {
  value: TReason;
  label: string;
  requiresComment?: boolean;
};

export type FeedbackDetails<TReason extends string> = {
  reasons: TReason[];
  comment?: string;
};

export type FeedbackLabels = {
  sectionAriaLabel: string;
  loading: string;
  prompt: string;
  positiveAriaLabel: string;
  negativeAriaLabel: string;
  negativeTitle: string;
  negativeDescription: string;
  commentLabel: string;
  commentDisclaimer: string;
  submit: string;
  success: string;
  saveError: string;
};

export type FeedbackProps<TReason extends string> = {
  initialView: FeedbackInitialView;
  reasons: ReadonlyArray<FeedbackReasonOption<TReason>>;
  onPositive: () => Promise<void>;
  onNegative: () => Promise<void>;
  onSubmitDetails: (details: FeedbackDetails<TReason>) => Promise<void>;
  initialSelectedReasons?: ReadonlyArray<TReason>;
  initialComment?: string;
  commentMaxLength?: number;
  labels?: Partial<FeedbackLabels>;
  onComplete?: () => void;
  sx?: SxProps<Theme>;
};

const DEFAULT_LABELS: FeedbackLabels = {
  sectionAriaLabel: 'Feedback',
  loading: 'Loading feedback…',
  prompt: 'Was this helpful?',
  positiveAriaLabel: 'This was helpful',
  negativeAriaLabel: 'This needs improvement',
  negativeTitle: 'Tell us what could be improved.',
  negativeDescription: 'Select all that apply.',
  commentLabel: 'Tell us more',
  commentDisclaimer: "Please don't include personal or sensitive information.",
  submit: 'Submit feedback',
  success: 'Thank you for your feedback.',
  saveError: 'We couldn’t save your feedback. Please try again.',
};

const DEFAULT_COMMENT_MAX_LENGTH = 500;
const EMPTY_SELECTED_REASONS: ReadonlyArray<never> = [];
const SELECTION_ANIMATION_DURATION_MS = {
  positive: 950,
  negative: 700,
} as const;

const LOTTIE_IDLE_ICON_COLOR = '[0.275,0.31,0.342]';
const LOTTIE_SUCCESS_COLOR = '[0.09,0.471,0.11]';
const LOTTIE_CRITICAL_COLOR = '[0.695,0.16,0.114]';

const hexToLottieColor = (color: string): [number, number, number] => {
  const hex = color.slice(1);
  return [
    Number.parseInt(hex.slice(0, 2), 16) / 255,
    Number.parseInt(hex.slice(2, 4), 16) / 255,
    Number.parseInt(hex.slice(4, 6), 16) / 255,
  ];
};

const createFeedbackAnimationData = (
  idleIconColor: string,
  successColor: string,
  criticalColor: string,
): typeof feedbackLottieAnimations => {
  const serializedAnimation = JSON.stringify(feedbackLottieAnimations)
    .replaceAll(LOTTIE_IDLE_ICON_COLOR, JSON.stringify(hexToLottieColor(idleIconColor)))
    .replaceAll(LOTTIE_SUCCESS_COLOR, JSON.stringify(hexToLottieColor(successColor)))
    .replaceAll(LOTTIE_CRITICAL_COLOR, JSON.stringify(hexToLottieColor(criticalColor)));
  return JSON.parse(serializedAnimation) as typeof feedbackLottieAnimations;
};

const getFeedbackButtonSx = (circleLeft: number): SxProps<Theme> => ({
  width: 40,
  height: 40,
  flex: '0 0 40px',
  position: 'relative',
  borderRadius: 'full',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 5,
    left: circleLeft,
    width: 30,
    height: 30,
    pointerEvents: 'none',
    borderRadius: 'full',
    bgcolor: 'background.default',
  },
  '&:hover::before': {
    bgcolor: 'action.hover',
  },
  '&.Mui-focusVisible::before': {
    boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
  },
});

function Feedback<TReason extends string>({
  initialView,
  reasons,
  onPositive,
  onNegative,
  onSubmitDetails,
  initialSelectedReasons = EMPTY_SELECTED_REASONS,
  initialComment = '',
  commentMaxLength = DEFAULT_COMMENT_MAX_LENGTH,
  labels,
  onComplete,
  sx,
}: FeedbackProps<TReason>): JSX.Element {
  const copy = { ...DEFAULT_LABELS, ...labels };
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const feedbackAnimationData = useMemo(
    () => createFeedbackAnimationData(theme.palette.icon.main, theme.palette.success.dark, theme.palette.critical.dark),
    [theme.palette.critical.dark, theme.palette.icon.main, theme.palette.success.dark],
  );
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const detailsEndRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollToDetailsRef = useRef(false);
  const [view, setView] = useState<FeedbackInitialView | 'thanks'>(initialView);
  const [selection, setSelection] = useState<'positive' | 'negative' | undefined>(
    initialView === 'details' ? 'negative' : undefined,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [selectedReasons, setSelectedReasons] = useState<TReason[]>([...initialSelectedReasons]);
  const [comment, setComment] = useState(initialComment);

  const playSelection = (selection: 'positive' | 'negative'): void => {
    const segment: [number, number] = selection === 'positive' ? [2, 30] : [31, 51];
    if (prefersReducedMotion) {
      lottieRef.current?.goToAndStop(segment[1], true);
      return;
    }
    lottieRef.current?.playSegments(segment, true);
  };

  const resetSelection = (): void => {
    const animation = lottieRef.current;
    animation?.animationItem?.resetSegments(true);
    animation?.goToAndStop(0, true);
  };

  const waitForSelectionAnimation = async (selectedAt: number, selected: 'positive' | 'negative'): Promise<void> => {
    if (prefersReducedMotion) return;

    const remainingDuration = SELECTION_ANIMATION_DURATION_MS[selected] - (Date.now() - selectedAt);
    if (remainingDuration > 0) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, remainingDuration);
      });
    }
  };

  const handlePositive = async (): Promise<void> => {
    if (isSaving) return;

    const selectedAt = Date.now();
    setSelection('positive');
    playSelection('positive');
    setIsSaving(true);
    setSaveError(undefined);
    try {
      await onPositive();
      await waitForSelectionAnimation(selectedAt, 'positive');
      setView('thanks');
      onComplete?.();
    } catch {
      resetSelection();
      setSelection(undefined);
      setView('rating');
      setSaveError(copy.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNegative = async (): Promise<void> => {
    if (isSaving) return;

    const selectedAt = Date.now();
    setSelection('negative');
    playSelection('negative');
    setIsSaving(true);
    setSaveError(undefined);
    try {
      await onNegative();
      await waitForSelectionAnimation(selectedAt, 'negative');
      shouldScrollToDetailsRef.current = true;
      setView('details');
    } catch {
      resetSelection();
      setSelection(undefined);
      setView('rating');
      setSaveError(copy.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const updateSelectedReasons = (nextReasons: TReason[]): void => {
    setSaveError(undefined);
    const stillRequiresComment = reasons.some((option) => option.requiresComment && nextReasons.includes(option.value));
    if (!stillRequiresComment) setComment('');
    setSelectedReasons(nextReasons);
  };

  const requiresComment = reasons.some((option) => option.requiresComment && selectedReasons.includes(option.value));
  const trimmedComment = comment.trim();
  const canSubmitDetails =
    selectedReasons.length > 0 &&
    (!requiresComment || (trimmedComment.length > 0 && comment.length <= commentMaxLength));

  useEffect(() => {
    if (view !== 'details' || !shouldScrollToDetailsRef.current) return;

    shouldScrollToDetailsRef.current = false;
    detailsEndRef.current?.scrollIntoView?.({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'end',
    });
  }, [prefersReducedMotion, view]);

  useEffect(() => {
    if (!requiresComment) return;

    detailsEndRef.current?.scrollIntoView?.({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'end',
    });
  }, [prefersReducedMotion, requiresComment]);

  const handleSubmitDetails = async (): Promise<void> => {
    if (!canSubmitDetails || isSaving) return;

    setIsSaving(true);
    setSaveError(undefined);
    try {
      await onSubmitDetails({
        reasons: selectedReasons,
        ...(requiresComment ? { comment: trimmedComment } : {}),
      });
      setView('thanks');
      onComplete?.();
    } catch {
      setSaveError(copy.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const isSelectionResult = view === 'details' || view === 'thanks';
  const resultText = view === 'details' ? copy.negativeTitle : view === 'thanks' ? copy.success : '';

  return (
    <Stack component='section' aria-label={copy.sectionAriaLabel} gap={2} sx={sx}>
      {view === 'loading' ? (
        <Stack direction='row' alignItems='center' gap={1.5} role='status'>
          <CircularProgress size={20} />
          <Typography variant='paragraphRegular' color='text.secondary'>
            {copy.loading}
          </Typography>
        </Stack>
      ) : null}

      {view === 'rating' || isSelectionResult ? (
        <Stack direction='row' alignItems='center'>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isSelectionResult ? '0fr' : '1fr',
              opacity: isSelectionResult ? 0 : 1,
              transition: 'grid-template-columns 300ms ease, opacity 150ms ease',
            }}
          >
            <Typography
              variant='captionRegular'
              color='text.primary'
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                pr: isSelectionResult ? 0 : 0.75,
                transition: 'padding-right 300ms ease',
              }}
            >
              {copy.prompt}
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'relative',
              width: isSelectionResult ? 30 : 88,
              height: 40,
              flexShrink: 0,
              overflow: 'hidden',
              lineHeight: 0,
              cursor: view === 'rating' ? 'pointer' : 'default',
              transition: 'width 300ms ease',
            }}
          >
            <Lottie
              lottieRef={lottieRef}
              animationData={feedbackAnimationData}
              autoplay={false}
              loop={false}
              aria-hidden
              onDOMLoaded={() => {
                if (initialView === 'details') lottieRef.current?.goToAndStop(51, true);
              }}
              style={{
                position: 'absolute',
                top: -3.38,
                left: isSelectionResult ? (selection === 'positive' ? -46.38 : -8.38) : 1.62,
                width: 84.77,
                height: 46.77,
                zIndex: 2,
                pointerEvents: 'none',
                transition: 'left 300ms ease',
              }}
            />
            <Stack
              direction='row'
              gap={1}
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                transform: selection === 'positive' && isSelectionResult ? 'translateX(-48px)' : 'none',
                transition: 'transform 300ms ease',
              }}
            >
              <ButtonBase
                aria-label={copy.negativeAriaLabel}
                aria-hidden={isSelectionResult && selection !== 'negative' ? true : undefined}
                disabled={isSaving || isSelectionResult}
                onClick={() => void handleNegative()}
                sx={getFeedbackButtonSx(isSelectionResult ? 0 : 10)}
              />
              <ButtonBase
                aria-label={copy.positiveAriaLabel}
                aria-hidden={isSelectionResult && selection !== 'positive' ? true : undefined}
                disabled={isSaving || isSelectionResult}
                onClick={() => void handlePositive()}
                sx={getFeedbackButtonSx(0)}
              />
            </Stack>
          </Box>
          <Typography
            role={view === 'thanks' ? 'status' : undefined}
            variant='paragraphMedium'
            color='text.primary'
            sx={{
              pl: isSelectionResult ? 2 : 0,
              maxWidth: isSelectionResult ? 480 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              opacity: isSelectionResult ? 1 : 0,
              transform: isSelectionResult ? 'translateX(0)' : 'translateX(-8px)',
              transition:
                'max-width 300ms ease, padding 300ms ease, opacity 200ms ease 150ms, transform 200ms ease 150ms',
            }}
          >
            {resultText}
          </Typography>
        </Stack>
      ) : null}

      {view === 'details' ? (
        <Stack gap={2}>
          <Card
            ref={detailsEndRef}
            elevation={2}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, borderRadius: 'xl', p: 2 }}
          >
            <Typography variant='paragraphRegular' color='text.primary'>
              {copy.negativeDescription}
            </Typography>
            <Box sx={{ '& [sharedcomponentid="Q_CHOICE_CHIPS"]': { gap: 1 } }}>
              <QChoiceChips
                items={[...reasons]}
                labelFromItem={(option) => option.label}
                keyFromItem={(option) => option.value}
                selectedItems={reasons.filter((option) => selectedReasons.includes(option.value))}
                onSelected={(options) => updateSelectedReasons(options.map((option) => option.value))}
                multiple
                disabled={isSaving}
              />
            </Box>
            {requiresComment ? (
              <TextField
                label={copy.commentLabel}
                value={comment}
                multiline
                minRows={3}
                disabled={isSaving}
                onChange={(event) => setComment(event.target.value.slice(0, commentMaxLength))}
                slotProps={{
                  inputLabel: {
                    sx: (theme) => ({ fontSize: theme.typography.paragraphRegular.fontSize }),
                  },
                  htmlInput: { maxLength: commentMaxLength },
                }}
                sx={(theme) => ({
                  '& .MuiInputBase-input': { fontSize: theme.typography.paragraphRegular.fontSize },
                })}
                error={comment.length > 0 && trimmedComment.length === 0}
                helperText={
                  <Stack component='span' direction='row' justifyContent='space-between' gap={1}>
                    <Typography variant='captionRegular' color='text.secondary'>
                      {copy.commentDisclaimer}
                    </Typography>
                    <Typography variant='captionRegular' color='text.secondary' sx={{ flexShrink: 0 }}>
                      {comment.length}/{commentMaxLength}
                    </Typography>
                  </Stack>
                }
              />
            ) : null}
            <Button
              variant='tonal'
              size='medium'
              disabled={!canSubmitDetails || isSaving}
              onClick={() => void handleSubmitDetails()}
              sx={{ alignSelf: 'flex-start' }}
            >
              {copy.submit}
            </Button>
          </Card>
        </Stack>
      ) : null}

      {saveError ? <Alert severity='error'>{saveError}</Alert> : null}
    </Stack>
  );
}

export default Feedback;
